/**
 * Storage utility functions for authentication tokens
 */

import { AuthTokens } from '../types/auth';

/**
 * Get stored tokens from localStorage
 */
export const getStoredTokens = (): AuthTokens | null => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const userId = localStorage.getItem('user_id');
    const isNewUser = localStorage.getItem('is_new_user');
    const expiresAt = localStorage.getItem('expires_at');
    
    if (accessToken && refreshToken && userId) {
      const tokens = { 
        access_token: accessToken, 
        refresh_token: refreshToken,
        user_id: userId,
        is_new_user: isNewUser === 'true',
        expires_at: expiresAt ? parseInt(expiresAt) : undefined
      };
      
      return tokens;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting stored tokens:', error);
    return null;
  }
};

/**
 * Clear tokens from localStorage
 */
export const clearTokens = (): void => {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('is_new_user');
    localStorage.removeItem('expires_at');
  } catch (error) {
    console.error('Error clearing tokens:', error);
  }
};

/**
 * Store tokens in localStorage
 */
export const storeTokens = (tokens: AuthTokens): void => {
  try {
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
    localStorage.setItem('user_id', tokens.user_id);
    if (tokens.is_new_user !== undefined) {
      localStorage.setItem('is_new_user', tokens.is_new_user.toString());
    }
    if (tokens.expires_at !== undefined) {
      localStorage.setItem('expires_at', tokens.expires_at.toString());
    }
  } catch (error) {
    console.error('Error storing tokens:', error);
  }
};

/**
 * Check if tokens exist in localStorage
 */
export const hasStoredTokens = (): boolean => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    return !!(accessToken && refreshToken);
  } catch (error) {
    console.error('Error checking stored tokens:', error);
    return false;
  }
};

/**
 * Get access token only
 */
export const getAccessToken = (): string | null => {
  try {
    return localStorage.getItem('access_token');
  } catch (error) {
    console.error('Error getting access token:', error);
    return null;
  }
};

/**
 * Get refresh token only
 */
export const getRefreshToken = (): string | null => {
  try {
    return localStorage.getItem('refresh_token');
  } catch (error) {
    console.error('Error getting refresh token:', error);
    return null;
  }
};
